import { createSlice } from '@reduxjs/toolkit';
import {
  fetchLeadsThunk,
  fetchLeadByIdThunk,
  createLeadThunk,
  updateLeadThunk,
  deleteLeadThunk,
  toggleFavouriteThunk,
  markAllReadThunk,
  sendMessageThunk,
  deleteMessageThunk,
  markMessageUnreadThunk,
  promoteLeadThunk,
  updateChecklistThunk,
  bulkUpdateStatusThunk,
  bulkDeleteThunk,
  bulkArchiveThunk,
  bulkJunkThunk,
  bulkMarkReadThunk,
  mergeLeadsThunk,
  fetchOnboardingLeadsThunk,
} from '../thunks/leadsThunks';

const initialState = {
  // List
  items: [],
  totalCount: 0,
  loading: false,
  error: null,

  // Pagination
  currentPage: 1,
  pageSize: 25,

  // Filters
  filters: {
    search: '',
    status: '',
    stage: 'lead',
    source: '',
    tag: '',
    assignee: '',
    sortBy: 'newest',
    dateRange: { from: null, to: null },
    contacted: '',
  },

  // Selected lead detail
  selectedLeadId: null,
  selectedLead: null,
  selectedLeadLoading: false,
  selectedLeadError: null,

  // UI state
  activeTab: 'messages',
  messageMode: 'email',
  showDetail: false,
  selectedIds: [],
  showEditModal: false,
  showArchive: false,
  showFavourites: false,

  // Sending
  sending: false,
  sendError: null,

  // Onboarding
  onboardingLeads: [],
  onboardingLoading: false,
  onboardingError: null,
  onboardingViewMode: 'list',
};

const leadsSlice = createSlice({
  name: 'leads',
  initialState,
  reducers: {
    setLeads: (state, action) => {
      state.items = action.payload;
    },
    prependLead: (state, action) => {
      state.items.unshift(action.payload);
      state.totalCount += 1;
    },
    updateLeadInList: (state, action) => {
      const { id, changes } = action.payload;
      const index = state.items.findIndex(lead => lead._id === id);
      if (index !== -1) {
        state.items[index] = { ...state.items[index], ...changes };
      }
    },
    removeLeadFromList: (state, action) => {
      state.items = state.items.filter(lead => lead._id !== action.payload);
      state.totalCount = Math.max(0, state.totalCount - 1);
    },
    removeLeadsFromList: (state, action) => {
      const idsToRemove = action.payload;
      state.items = state.items.filter(lead => !idsToRemove.includes(lead._id));
      state.totalCount = Math.max(0, state.totalCount - idsToRemove.length);
    },
    setFilters: (state, action) => {
      state.filters = { ...state.filters, ...action.payload };
    },
    setSearch: (state, action) => {
      state.filters.search = action.payload;
    },
    setStatusFilter: (state, action) => {
      state.filters.status = action.payload;
    },
    setStageFilter: (state, action) => {
      state.filters.stage = action.payload;
    },
    setSourceFilter: (state, action) => {
      state.filters.source = action.payload;
    },
    setTagFilter: (state, action) => {
      state.filters.tag = action.payload;
    },
    setAssigneeFilter: (state, action) => {
      state.filters.assignee = action.payload;
    },
    setSortBy: (state, action) => {
      state.filters.sortBy = action.payload;
    },
    setDateRange: (state, action) => {
      state.filters.dateRange = action.payload;
    },
    setContacted: (state, action) => {
      state.filters.contacted = action.payload;
    },
    resetFilters: (state) => {
      state.filters = initialState.filters;
      state.currentPage = 1;
    },
    setCurrentPage: (state, action) => {
      state.currentPage = action.payload;
    },
    setPageSize: (state, action) => {
      state.pageSize = action.payload;
      state.currentPage = 1;
    },
    setSelectedLeadId: (state, action) => {
      state.selectedLeadId = action.payload;
    },
    setSelectedLead: (state, action) => {
      state.selectedLead = action.payload;
    },
    clearSelectedLead: (state) => {
      state.selectedLeadId = null;
      state.selectedLead = null;
      state.selectedLeadError = null;
    },
    setActiveTab: (state, action) => {
      state.activeTab = action.payload;
    },
    setMessageMode: (state, action) => {
      state.messageMode = action.payload;
    },
    setShowDetail: (state, action) => {
      state.showDetail = action.payload;
    },
    toggleSelectId: (state, action) => {
      const id = action.payload;
      const index = state.selectedIds.indexOf(id);
      if (index === -1) {
        state.selectedIds.push(id);
      } else {
        state.selectedIds.splice(index, 1);
      }
    },
    selectAllIds: (state, action) => {
      state.selectedIds = action.payload;
    },
    clearSelectedIds: (state) => {
      state.selectedIds = [];
    },
    setShowEditModal: (state, action) => {
      state.showEditModal = action.payload;
    },
    setShowArchive: (state, action) => {
      state.showArchive = action.payload;
    },
    setShowFavourites: (state, action) => {
      state.showFavourites = action.payload;
    },
    appendMessage: (state, action) => {
      if (state.selectedLead?.messages) {
        state.selectedLead.messages.push(action.payload);
      }
    },
    removeMessage: (state, action) => {
      if (state.selectedLead?.messages) {
        state.selectedLead.messages = state.selectedLead.messages.filter(
          msg => msg.dateTime !== action.payload
        );
      }
    },
    markMessageUnread: (state, action) => {
      if (state.selectedLead?.messages) {
        const message = state.selectedLead.messages.find(msg => msg.dateTime === action.payload);
        if (message) {
          message.read = false;
        }
      }
    },
    appendNote: (state, action) => {
      if (state.selectedLead?.notes) {
        state.selectedLead.notes.push(action.payload);
      }
    },
    removeNote: (state, action) => {
      if (state.selectedLead?.notes) {
        state.selectedLead.notes = state.selectedLead.notes.filter(
          note => note.id !== action.payload
        );
      }
    },
    updateNote: (state, action) => {
      const { noteId, text } = action.payload;
      if (state.selectedLead?.notes) {
        const note = state.selectedLead.notes.find(n => n.id === noteId);
        if (note) {
          note.text = text;
        }
      }
    },
    updateChecklistItem: (state, action) => {
      const { field, value } = action.payload;
      if (state.selectedLead?.checklist) {
        state.selectedLead.checklist[field] = value;
      }
    },
    optimisticUpdateLead: (state, action) => {
      const { id, changes } = action.payload;
      const index = state.items.findIndex(lead => lead._id === id);
      if (index !== -1) {
        state.items[index] = { ...state.items[index], ...changes };
      }
      if (state.selectedLead?._id === id) {
        state.selectedLead = { ...state.selectedLead, ...changes };
      }
    },
    rollbackLead: (state, action) => {
      const previousLead = action.payload;
      const index = state.items.findIndex(lead => lead._id === previousLead._id);
      if (index !== -1) {
        state.items[index] = previousLead;
      }
      if (state.selectedLead?._id === previousLead._id) {
        state.selectedLead = previousLead;
      }
    },
    setOnboardingLeads: (state, action) => {
      state.onboardingLeads = action.payload;
    },
    setOnboardingViewMode: (state, action) => {
      state.onboardingViewMode = action.payload;
    },
    updateOnboardingLead: (state, action) => {
      const { id, changes } = action.payload;
      const index = state.onboardingLeads.findIndex(lead => lead._id === id);
      if (index !== -1) {
        state.onboardingLeads[index] = { ...state.onboardingLeads[index], ...changes };
      }
    },
  },
  extraReducers: (builder) => {
    builder
      // fetchLeadsThunk
      .addCase(fetchLeadsThunk.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchLeadsThunk.fulfilled, (state, action) => {
        state.items = action.payload.items || [];
        state.totalCount = action.payload.totalCount || 0;
        state.loading = false;
      })
      .addCase(fetchLeadsThunk.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error.message;
      })

      // fetchLeadByIdThunk
      .addCase(fetchLeadByIdThunk.pending, (state) => {
        state.selectedLeadLoading = true;
        state.selectedLeadError = null;
      })
      .addCase(fetchLeadByIdThunk.fulfilled, (state, action) => {
        state.selectedLead = action.payload;
        state.selectedLeadId = action.payload._id;
        state.selectedLeadLoading = false;
      })
      .addCase(fetchLeadByIdThunk.rejected, (state, action) => {
        state.selectedLeadLoading = false;
        state.selectedLeadError = action.error.message;
      })

      // createLeadThunk
      .addCase(createLeadThunk.fulfilled, (state, action) => {
        state.items.unshift(action.payload);
        state.totalCount += 1;
      })

      // updateLeadThunk
      .addCase(updateLeadThunk.fulfilled, (state, action) => {
        const updated = action.payload;
        const index = state.items.findIndex(lead => lead._id === updated._id);
        if (index !== -1) {
          state.items[index] = updated;
        }
        if (state.selectedLead?._id === updated._id) {
          state.selectedLead = updated;
        }
      })

      // deleteLeadThunk
      .addCase(deleteLeadThunk.fulfilled, (state, action) => {
        const id = action.meta.arg;
        state.items = state.items.filter(lead => lead._id !== id);
        state.totalCount = Math.max(0, state.totalCount - 1);
      })

      // toggleFavouriteThunk
      .addCase(toggleFavouriteThunk.fulfilled, (state, action) => {
        if (state.selectedLead) {
          state.selectedLead.favourite = action.payload.favourite;
        }
      })

      // markAllReadThunk
      .addCase(markAllReadThunk.fulfilled, (state) => {
        if (state.selectedLead?.messages) {
          state.selectedLead.messages.forEach(msg => {
            msg.read = true;
          });
        }
      })

      // sendMessageThunk
      .addCase(sendMessageThunk.pending, (state) => {
        state.sending = true;
        state.sendError = null;
      })
      .addCase(sendMessageThunk.fulfilled, (state, action) => {
        if (state.selectedLead?.messages) {
          state.selectedLead.messages.push(action.payload);
        }
        state.sending = false;
      })
      .addCase(sendMessageThunk.rejected, (state, action) => {
        state.sending = false;
        state.sendError = action.error.message;
      })

      // deleteMessageThunk
      .addCase(deleteMessageThunk.fulfilled, (state, action) => {
        const dateTime = action.meta.arg.dateTime;
        if (state.selectedLead?.messages) {
          state.selectedLead.messages = state.selectedLead.messages.filter(
            msg => msg.dateTime !== dateTime
          );
        }
      })

      // markMessageUnreadThunk
      .addCase(markMessageUnreadThunk.fulfilled, (state, action) => {
        const dateTime = action.meta.arg;
        if (state.selectedLead?.messages) {
          const message = state.selectedLead.messages.find(msg => msg.dateTime === dateTime);
          if (message) {
            message.read = false;
          }
        }
      })

      // promoteLeadThunk
      .addCase(promoteLeadThunk.fulfilled, (state, action) => {
        if (state.selectedLead) {
          state.selectedLead.stage = 'onboarding';
        }
      })

      // updateChecklistThunk
      .addCase(updateChecklistThunk.fulfilled, (state, action) => {
        if (state.selectedLead) {
          state.selectedLead.checklist = action.payload.checklist;
        }
      })

      // bulkUpdateStatusThunk
      .addCase(bulkUpdateStatusThunk.fulfilled, (state, action) => {
        const ids = action.meta.arg.ids;
        state.items = state.items.filter(lead => !ids.includes(lead._id));
        state.totalCount = Math.max(0, state.totalCount - ids.length);
      })

      // bulkDeleteThunk
      .addCase(bulkDeleteThunk.fulfilled, (state, action) => {
        const ids = action.meta.arg;
        state.items = state.items.filter(lead => !ids.includes(lead._id));
        state.totalCount = Math.max(0, state.totalCount - ids.length);
      })

      // bulkArchiveThunk
      .addCase(bulkArchiveThunk.fulfilled, (state, action) => {
        const ids = action.meta.arg;
        state.items = state.items.filter(lead => !ids.includes(lead._id));
        state.totalCount = Math.max(0, state.totalCount - ids.length);
      })

      // bulkJunkThunk
      .addCase(bulkJunkThunk.fulfilled, (state, action) => {
        const ids = action.meta.arg;
        state.items = state.items.filter(lead => !ids.includes(lead._id));
        state.totalCount = Math.max(0, state.totalCount - ids.length);
      })

      // bulkMarkReadThunk
      .addCase(bulkMarkReadThunk.fulfilled, (state, action) => {
        const ids = action.meta.arg;
        state.items.forEach(lead => {
          if (ids.includes(lead._id) && lead.messages) {
            lead.messages.forEach(msg => {
              msg.read = true;
            });
          }
        });
      })

      // mergeLeadsThunk
      .addCase(mergeLeadsThunk.fulfilled, (state) => {
        // Re-fetch needed - clearing current state
        state.items = [];
        state.totalCount = 0;
      })

      // fetchOnboardingLeadsThunk
      .addCase(fetchOnboardingLeadsThunk.pending, (state) => {
        state.onboardingLoading = true;
        state.onboardingError = null;
      })
      .addCase(fetchOnboardingLeadsThunk.fulfilled, (state, action) => {
        state.onboardingLeads = action.payload;
        state.onboardingLoading = false;
      })
      .addCase(fetchOnboardingLeadsThunk.rejected, (state, action) => {
        state.onboardingLoading = false;
        state.onboardingError = action.error.message;
      });
  },
});

export const {
  setLeads,
  prependLead,
  updateLeadInList,
  removeLeadFromList,
  removeLeadsFromList,
  setFilters,
  setSearch,
  setStatusFilter,
  setStageFilter,
  setSourceFilter,
  setTagFilter,
  setAssigneeFilter,
  setSortBy,
  setDateRange,
  setContacted,
  resetFilters,
  setCurrentPage,
  setPageSize,
  setSelectedLeadId,
  setSelectedLead,
  clearSelectedLead,
  setActiveTab,
  setMessageMode,
  setShowDetail,
  toggleSelectId,
  selectAllIds,
  clearSelectedIds,
  setShowEditModal,
  setShowArchive,
  setShowFavourites,
  appendMessage,
  removeMessage,
  markMessageUnread,
  appendNote,
  removeNote,
  updateNote,
  updateChecklistItem,
  optimisticUpdateLead,
  rollbackLead,
  setOnboardingLeads,
  setOnboardingViewMode,
  updateOnboardingLead,
} = leadsSlice.actions;

export default leadsSlice.reducer;
